import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import { Toaster } from "../components/ui/sonner";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, ArrowLeft, Calendar, ExternalLink, Lock, Users, Mail } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Login Component
const LoginForm = ({ onLogin }) => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!username || !password) {
      toast.error("Please enter both email and password");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login`, {
        username,
        password,
      });
      if (response.data.success) {
        // Store credentials for Basic Auth
        const credentials = btoa(`${username}:${password}`);
        localStorage.setItem("jdf_admin_auth", credentials);
        onLogin(credentials);
        toast.success("Login successful");
      }
    } catch (error) {
      toast.error("Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-bone flex items-center justify-center px-6">
      <Card className="w-full max-w-md" data-testid="login-card">
        <CardHeader className="text-center">
          <div className="w-16 h-16 bg-sage/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Lock className="w-8 h-8 text-sage" />
          </div>
          <CardTitle className="font-heading text-2xl">Admin Login</CardTitle>
          <CardDescription className="font-body">
            Please enter your credentials to access the admin dashboard.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleLogin} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="username">Email</Label>
              <Input
                id="username"
                type="email"
                placeholder="Enter your email"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                data-testid="login-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password-input"
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-sage hover:bg-sage/90"
              data-testid="login-btn"
            >
              {loading ? "Logging in..." : "Login"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [authToken, setAuthToken] = useState(null);
  const [events, setEvents] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    start_date: "",
    end_date: "",
    mode: "Online",
    registration_link: "https://forms.gle/yco5zWDZo8Hm45WTA",
    is_active: true,
  });

  // Check for stored auth on mount
  useEffect(() => {
    const storedAuth = localStorage.getItem("jdf_admin_auth");
    if (storedAuth) {
      setAuthToken(storedAuth);
    } else {
      setLoading(false);
    }
  }, []);

  // Create axios instance with auth
  const getAuthHeaders = () => ({
    headers: {
      Authorization: `Basic ${authToken}`,
    },
  });

 const fetchData = useCallback(async () => {
  if (!authToken) return;

  try {
    const [eventsRes, subscribersRes] = await Promise.all([
      axios.get(`${API}/events`, {
        headers: { Authorization: `Basic ${authToken}` },
      }),
      axios.get(`${API}/subscribers`, {
        headers: { Authorization: `Basic ${authToken}` },
      }),
    ]);

    setEvents(eventsRes.data);
    setSubscribers(subscribersRes.data);
  } catch (error) {
    console.error("Error fetching data:", error);

    if (error.response?.status === 401) {
      localStorage.removeItem("jdf_admin_auth");
      setAuthToken(null);
      toast.error("Session expired. Please login again.");
    } else {
      toast.error("Failed to load data");
    }
  } finally {
    setLoading(false);
  }
}, [authToken]);

useEffect(() => {
  if (authToken) {
    fetchData();
  }
}, [authToken, fetchData]);

  const handleLogout = () => {
    localStorage.removeItem("jdf_admin_auth");
    setAuthToken(null);
    toast.success("Logged out successfully");
  };

  const resetForm = () => {
    setFormData({
      title: "",
      description: "",
      start_date: "",
      end_date: "",
      mode: "Online",
      registration_link: "https://forms.gle/yco5zWDZo8Hm45WTA",
      is_active: true,
    });
  };

  const handleCreate = async () => {
    if (!formData.title || !formData.start_date || !formData.end_date) {
      toast.error("Please fill in required fields");
      return;
    }

    try {
      await axios.post(`${API}/events`, formData, getAuthHeaders());
      toast.success("Event created successfully");
      setIsCreateOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error("Error creating event:", error);
      toast.error("Failed to create event");
    }
  };

  const handleEdit = async () => {
    if (!selectedEvent) return;

    try {
      await axios.put(`${API}/events/${selectedEvent.id}`, formData, getAuthHeaders());
      toast.success("Event updated successfully");
      setIsEditOpen(false);
      setSelectedEvent(null);
      resetForm();
      fetchData();
    } catch (error) {
      console.error("Error updating event:", error);
      toast.error("Failed to update event");
    }
  };

  const handleDelete = async () => {
    if (!selectedEvent) return;

    try {
      await axios.delete(`${API}/events/${selectedEvent.id}`, getAuthHeaders());
      toast.success("Event deleted successfully");
      setIsDeleteOpen(false);
      setSelectedEvent(null);
      fetchData();
    } catch (error) {
      console.error("Error deleting event:", error);
      toast.error("Failed to delete event");
    }
  };

  const openEditDialog = (event) => {
    setSelectedEvent(event);
    setFormData({
      title: event.title,
      description: event.description || "",
      start_date: event.start_date,
      end_date: event.end_date,
      mode: event.mode,
      registration_link: event.registration_link,
      is_active: event.is_active,
    });
    setIsEditOpen(true);
  };

  const openDeleteDialog = (event) => {
    setSelectedEvent(event);
    setIsDeleteOpen(true);
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  // Show login form if not authenticated
  if (!authToken) {
    return (
      <>
        <Toaster position="top-right" richColors />
        <LoginForm onLogin={setAuthToken} />
      </>
    );
  }

  return (
    <div className="min-h-screen bg-bone" data-testid="admin-dashboard">
      <Toaster position="top-right" richColors />
      
      {/* Header */}
      <header className="bg-white border-b border-border/50 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate("/")}
              data-testid="back-to-home-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="font-heading text-2xl font-semibold text-charcoal">
                Admin Dashboard
              </h1>
              <p className="font-body text-sm text-muted-foreground">
                Manage workshop events & subscribers
              </p>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
              <DialogTrigger asChild>
                <Button
                  className="bg-sage hover:bg-sage/90 text-white rounded-full"
                  data-testid="create-event-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Event
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-lg" data-testid="create-event-dialog">
                <DialogHeader>
                  <DialogTitle className="font-heading text-xl">
                    Create New Event
                  </DialogTitle>
                  <DialogDescription className="font-body">
                    Add a new workshop event for participants to register.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label htmlFor="title">Title *</Label>
                    <Input
                      id="title"
                      placeholder="JDF Life Workshop - January 2025"
                      value={formData.title}
                      onChange={(e) =>
                        setFormData({ ...formData, title: e.target.value })
                      }
                      data-testid="event-title-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="description">Description</Label>
                    <Textarea
                      id="description"
                      placeholder="Optional description..."
                      value={formData.description}
                      onChange={(e) =>
                        setFormData({ ...formData, description: e.target.value })
                      }
                      data-testid="event-description-input"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="start_date">Start Date *</Label>
                      <Input
                        id="start_date"
                        type="date"
                        value={formData.start_date}
                        onChange={(e) =>
                          setFormData({ ...formData, start_date: e.target.value })
                        }
                        data-testid="event-start-date-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="end_date">End Date *</Label>
                      <Input
                        id="end_date"
                        type="date"
                        value={formData.end_date}
                        onChange={(e) =>
                          setFormData({ ...formData, end_date: e.target.value })
                        }
                        data-testid="event-end-date-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="mode">Mode</Label>
                    <Select
                      value={formData.mode}
                      onValueChange={(value) =>
                        setFormData({ ...formData, mode: value })
                      }
                    >
                      <SelectTrigger data-testid="event-mode-select">
                        <SelectValue placeholder="Select mode" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Online">Online</SelectItem>
                        <SelectItem value="Offline">Offline</SelectItem>
                        <SelectItem value="Hybrid">Hybrid</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="registration_link">Registration Link</Label>
                    <Input
                      id="registration_link"
                      placeholder="https://forms.gle/..."
                      value={formData.registration_link}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          registration_link: e.target.value,
                        })
                      }
                      data-testid="event-registration-link-input"
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label htmlFor="is_active">Active (Visible on landing page)</Label>
                    <Switch
                      id="is_active"
                      checked={formData.is_active}
                      onCheckedChange={(checked) =>
                        setFormData({ ...formData, is_active: checked })
                      }
                      data-testid="event-active-switch"
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setIsCreateOpen(false);
                      resetForm();
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    className="bg-sage hover:bg-sage/90"
                    onClick={handleCreate}
                    data-testid="save-event-btn"
                  >
                    Create Event
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            <Button
              variant="outline"
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        <Tabs defaultValue="events" className="space-y-6">
          <TabsList>
            <TabsTrigger value="events" className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              Events
            </TabsTrigger>
            <TabsTrigger value="subscribers" className="flex items-center gap-2">
              <Users className="w-4 h-4" />
              Subscribers ({subscribers.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="events">
            <div className="bg-white rounded-xl border border-border/50 overflow-hidden">
              {loading ? (
                <div className="p-12 text-center text-muted-foreground">
                  Loading events...
                </div>
              ) : events.length === 0 ? (
                <div className="p-12 text-center" data-testid="no-events-message">
                  <Calendar className="w-12 h-12 text-muted-foreground/50 mx-auto mb-4" />
                  <p className="font-body text-muted-foreground mb-4">
                    No events created yet.
                  </p>
                  <Button
                    className="bg-sage hover:bg-sage/90 text-white rounded-full"
                    onClick={() => setIsCreateOpen(true)}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Create First Event
                  </Button>
                </div>
              ) : (
                <Table data-testid="events-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="font-body">Title</TableHead>
                      <TableHead className="font-body">Dates</TableHead>
                      <TableHead className="font-body">Mode</TableHead>
                      <TableHead className="font-body">Status</TableHead>
                      <TableHead className="font-body text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {events.map((event, index) => (
                      <TableRow key={event.id} data-testid={`event-row-${index}`}>
                        <TableCell className="font-medium">
                          <div>
                            <p className="font-body text-charcoal">{event.title}</p>
                            {event.description && (
                              <p className="font-body text-xs text-muted-foreground truncate max-w-xs">
                                {event.description}
                              </p>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="font-body text-sm text-muted-foreground">
                          {formatDate(event.start_date)} to {formatDate(event.end_date)}
                        </TableCell>
                        <TableCell>
                          <span className="font-body text-xs bg-muted px-2 py-1 rounded-full">
                            {event.mode}
                          </span>
                        </TableCell>
                        <TableCell>
                          <span
                            className={`font-body text-xs px-2 py-1 rounded-full ${
                              event.is_active
                                ? "bg-sage/10 text-sage"
                                : "bg-muted text-muted-foreground"
                            }`}
                          >
                            {event.is_active ? "Active" : "Inactive"}
                          </span>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            <a
                              href={event.registration_link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-2 hover:bg-muted rounded-md transition-colors"
                              title="Open registration link"
                            >
                              <ExternalLink className="w-4 h-4 text-muted-foreground" />
                            </a>
                            <button
                              onClick={() => openEditDialog(event)}
                              className="p-2 hover:bg-muted rounded-md transition-colors"
                              data-testid={`edit-event-btn-${index}`}
                            >
                              <Pencil className="w-4 h-4 text-muted-foreground" />
                            </button>
                            <button
                              onClick={() => openDeleteDialog(event)}
                              className="p-2 hover:bg-destructive/10 rounded-md transition-colors"
                              data-testid={`delete-event-btn-${index}`}
                            >
                              <Trash2 className="w-4 h-4 text-destructive" />
                            </button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          </TabsContent>

          <TabsContent value="subscribers">
            <div className="bg-white rounded-xl border border-border/50 overflow-hidden">
              {loading ? (
                <div className="p-12 text-center text-muted-foreground">
                  Loading subscribers...
                </div>
              ) : subscribers.length === 0 ? (
                <div className="p-12 text-center" data-testid="no-subscribers-message">
                  <Mail className="w-12 h-12 text-muted-foreground/50 mx-auto mb-4" />
                  <p className="font-body text-muted-foreground">
                    No subscribers yet.
                  </p>
                </div>
              ) : (
                <Table data-testid="subscribers-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="font-body">Name</TableHead>
                      <TableHead className="font-body">Email</TableHead>
                      <TableHead className="font-body">Subscribed On</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {subscribers.map((subscriber, index) => (
                      <TableRow key={subscriber.id} data-testid={`subscriber-row-${index}`}>
                        <TableCell className="font-body text-charcoal">
                          {subscriber.name || "—"}
                        </TableCell>
                        <TableCell className="font-body text-muted-foreground">
                          {subscriber.email}
                        </TableCell>
                        <TableCell className="font-body text-sm text-muted-foreground">
                          {new Date(subscriber.subscribed_at).toLocaleDateString("en-US", {
                            month: "short",
                            day: "numeric",
                            year: "numeric",
                          })}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* Edit Dialog */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="edit-event-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Edit Event</DialogTitle>
            <DialogDescription className="font-body">
              Update the event details.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit-title">Title *</Label>
              <Input
                id="edit-title"
                value={formData.title}
                onChange={(e) =>
                  setFormData({ ...formData, title: e.target.value })
                }
                data-testid="edit-event-title-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-description">Description</Label>
              <Textarea
                id="edit-description"
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                data-testid="edit-event-description-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-start_date">Start Date *</Label>
                <Input
                  id="edit-start_date"
                  type="date"
                  value={formData.start_date}
                  onChange={(e) =>
                    setFormData({ ...formData, start_date: e.target.value })
                  }
                  data-testid="edit-event-start-date-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-end_date">End Date *</Label>
                <Input
                  id="edit-end_date"
                  type="date"
                  value={formData.end_date}
                  onChange={(e) =>
                    setFormData({ ...formData, end_date: e.target.value })
                  }
                  data-testid="edit-event-end-date-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-mode">Mode</Label>
              <Select
                value={formData.mode}
                onValueChange={(value) =>
                  setFormData({ ...formData, mode: value })
                }
              >
                <SelectTrigger data-testid="edit-event-mode-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Online">Online</SelectItem>
                  <SelectItem value="Offline">Offline</SelectItem>
                  <SelectItem value="Hybrid">Hybrid</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-registration_link">Registration Link</Label>
              <Input
                id="edit-registration_link"
                value={formData.registration_link}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    registration_link: e.target.value,
                  })
                }
                data-testid="edit-event-registration-link-input"
              />
            </div>
            <div className="flex items-center justify-between">
              <Label htmlFor="edit-is_active">Active</Label>
              <Switch
                id="edit-is_active"
                checked={formData.is_active}
                onCheckedChange={(checked) =>
                  setFormData({ ...formData, is_active: checked })
                }
                data-testid="edit-event-active-switch"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsEditOpen(false);
                setSelectedEvent(null);
                resetForm();
              }}
            >
              Cancel
            </Button>
            <Button
              className="bg-sage hover:bg-sage/90"
              onClick={handleEdit}
              data-testid="update-event-btn"
            >
              Update Event
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
        <DialogContent className="sm:max-w-md" data-testid="delete-event-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Delete Event</DialogTitle>
            <DialogDescription className="font-body">
              Are you sure you want to delete "{selectedEvent?.title}"? This
              action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setIsDeleteOpen(false);
                setSelectedEvent(null);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              data-testid="confirm-delete-btn"
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
